import { network } from '@root/utils'
import { ExpenseClaimTag } from './expenseClaims'
import { handleReturn } from './root'

export type ExpenseVehicle = 'CAR' | 'MOTORCYCLE' | 'MOPED' | 'COMPANY_CAR' | 'BICYCLE'

export type ExpenseDistanceSource = 'MANUAL' | 'ROUTE'

export type ExpenseClaimLeg = {
  id: number
  position: number
  departure_address: string
  destination_address: string
  departure_at: string
  arrival_at: string
  description: string
  vehicle: ExpenseVehicle
  distance_km: string
  route_distance_km: string | null
  distance_source: ExpenseDistanceSource
  extra_passengers: number
  has_trailer: boolean
  has_heavy_load: boolean
  country_change: string | null
  rate_per_km: string
  amount: number
}

export type ExpenseTravelDayAllowance =
  | 'NONE'
  | 'PARTIAL'
  | 'FULL'
  | 'FOREIGN_FULL'
  | 'FOREIGN_HALF'

export type ExpenseClaimTravelDay = {
  id: number
  day_index: number
  starts_at: string
  ends_at: string
  duration_minutes: number
  destination: string
  destination_label: string
  destination_override: string | null
  allowance_type: ExpenseTravelDayAllowance
  free_meals: number
  meal_allowance: boolean
  manual_amount: number | null
  rate: string
  amount: number
  tax_free_amount: number
}

export type PerDiemDestination = {
  key: string
  label: string
  amount: string
  is_domestic: boolean
}

export type ExpenseClaimTrip = {
  id: number
  description: string
  start_at: string
  end_at: string
  destination: string
  auto_per_diem: boolean
  per_diem_automatic_allowed: boolean
  position: number
  tags: ExpenseClaimTag[]
  legs: ExpenseClaimLeg[]
  travel_days: ExpenseClaimTravelDay[]
  expenses: {
    id: number
    merchant: string
    purchase_date: string | null
    amount: number | null
    category: string | null
  }[]
  distance_km: string
  mileage_amount: number
  per_diem_amount: number
  expense_amount: number
}

export type ExpenseClaimTripWrite = {
  description: string
  start_at: string
  end_at: string
  destination: string
  auto_per_diem?: boolean
  tag_ids?: number[]
}

export type ExpenseClaimLegWrite = {
  departure_address?: string
  destination_address: string
  departure_at: string
  arrival_at: string
  description?: string
  vehicle: ExpenseVehicle
  distance_km: string
  route_distance_km?: string | null
  distance_source?: ExpenseDistanceSource
  extra_passengers?: number
  has_trailer?: boolean
  has_heavy_load?: boolean
  country_change?: string | null
  position?: number
}

export type AddressSuggestion = {
  place_id: string
  text: string
}

export type RouteDistance = {
  distance_km: string | null
  code?: 'unavailable' | 'not_found'
}

const tripsUrl = (slug: string, claimId: number) =>
  `/v1/business/${slug}/expense_claims/${claimId}/trips/`

export const fetchExpenseClaimTrips = async (
  slug: string,
  claimId: number
): Promise<ExpenseClaimTrip[]> => {
  const { status, data } = await network.httpClient.request<{ results: ExpenseClaimTrip[] }>({
    url: tripsUrl(slug, claimId),
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data }).results
}

export const fetchExpenseClaimTrip = async (
  slug: string,
  claimId: number,
  tripId: number
): Promise<ExpenseClaimTrip> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimTrip>({
    url: `${tripsUrl(slug, claimId)}${tripId}/`,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const createExpenseClaimTrip = async (
  slug: string,
  claimId: number,
  payload: ExpenseClaimTripWrite
): Promise<ExpenseClaimTrip> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimTrip>({
    url: tripsUrl(slug, claimId),
    method: 'POST',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const updateExpenseClaimTrip = async (
  slug: string,
  claimId: number,
  tripId: number,
  payload: Partial<ExpenseClaimTripWrite>
): Promise<ExpenseClaimTrip> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimTrip>({
    url: `${tripsUrl(slug, claimId)}${tripId}/`,
    method: 'PATCH',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

const legsUrl = (slug: string, claimId: number, tripId: number) =>
  `${tripsUrl(slug, claimId)}${tripId}/legs/`

export const createExpenseClaimLeg = async (
  slug: string,
  claimId: number,
  tripId: number,
  payload: ExpenseClaimLegWrite
): Promise<ExpenseClaimLeg> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimLeg>({
    url: legsUrl(slug, claimId, tripId),
    method: 'POST',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const updateExpenseClaimLeg = async (
  slug: string,
  claimId: number,
  tripId: number,
  legId: number,
  payload: Partial<ExpenseClaimLegWrite>
): Promise<ExpenseClaimLeg> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimLeg>({
    url: `${legsUrl(slug, claimId, tripId)}${legId}/`,
    method: 'PATCH',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteExpenseClaimLeg = async (
  slug: string,
  claimId: number,
  tripId: number,
  legId: number
): Promise<void> => {
  const { status, data } = await network.httpClient.request({
    url: `${legsUrl(slug, claimId, tripId)}${legId}/`,
    method: 'DELETE',
    validateStatus: () => true
  })
  handleReturn({ status, data })
}

export const fetchAddressSuggestions = async (
  slug: string,
  query: string,
  session: string
): Promise<AddressSuggestion[]> => {
  const { status, data } = await network.httpClient.request<{ results: AddressSuggestion[] }>({
    url: `/v1/business/${slug}/expense_claims/address_suggestions/`,
    method: 'GET',
    params: { q: query, session },
    validateStatus: () => true
  })
  return handleReturn({ status, data }).results
}

export const fetchRouteDistance = async (
  slug: string,
  from: string,
  to: string
): Promise<RouteDistance> => {
  const { status, data } = await network.httpClient.request<RouteDistance>({
    url: `/v1/business/${slug}/expense_claims/route_distance/`,
    method: 'GET',
    params: { from, to },
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const fetchPerDiemDestinations = async (
  slug: string,
  on: string
): Promise<{ domestic_key: string; results: PerDiemDestination[] }> => {
  const { status, data } = await network.httpClient.request<{
    domestic_key: string
    results: PerDiemDestination[]
  }>({
    url: `/v1/business/${slug}/expense_claims/per_diem_destinations/`,
    method: 'GET',
    params: { on },
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const patchExpenseClaimTravelDay = async (
  slug: string,
  claimId: number,
  tripId: number,
  dayId: number,
  payload: {
    free_meals?: number
    manual_amount?: number | null
    meal_allowance?: boolean
    destination_override?: string | null
  }
): Promise<ExpenseClaimTravelDay> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimTravelDay>({
    url: `${tripsUrl(slug, claimId)}${tripId}/travel_days/${dayId}/`,
    method: 'PATCH',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}
